// europe-west8 (test) is NOT part of the eur3 multi-region the Firestore database lives in — a v2// Firestore trigger deployed there would fail (Eventarc requires the trigger location to match the// database location). Fine today since no trigger uses FUNCTION_REGIONS.test; don't add one without// checking this first.
export const FUNCTION_REGIONS = { prod: 'europe-west1', test: 'europe-west8' } as const;
export type FunctionsVersion = 'Prod' | 'Test';

/**
 * Concrete base URLs for each deployed Cloud Run function, keyed by version.
 * These replace the old pattern-based `functionsDomain + baseName + version` scheme.
 * The emulator URL is built dynamically in getApiUrl() when emulatorDomain is provided.
 */
export const FUNCTION_BASE_URLS: Record<FunctionsVersion, Record<string, string>> = {
    Prod: {
        bookingApi: 'https://bookingapiprod-g5ph7wf6ca-ew.a.run.app',
        crmApi: 'https://crmapiprod-g5ph7wf6ca-ew.a.run.app',
        couponApi: 'https://couponapiprod-g5ph7wf6ca-ew.a.run.app',
        devModeApi: 'https://devmodeapiprod-g5ph7wf6ca-ew.a.run.app',
        paymentsApi: 'https://paymentsapiprod-g5ph7wf6ca-ew.a.run.app',
        // Real URL from the first deploy (2026-08-31), gateway form like payAtTable below — NOT the
        // bare *.run.app host the older functions use. The DEM providers are configured with this
        // URL, so a wrong host means silently missed unsubscribes.
        demApi: 'https://europe-west1-scb-website.cloudfunctions.net/demApiProd',
        // Real URL from the first deploy (2026-08-25). Note the DIFFERENT host form from the older
        // functions above: newly-deployed v2 functions get the cloudfunctions.net gateway URL rather
        // than a bare *.run.app hostname. Both are valid; don't "normalize" this to match the others.
        payAtTableApi: 'https://europe-west1-scb-website.cloudfunctions.net/payAtTableApiProd',
    },
    Test: {
        bookingApi: 'https://bookingapitest-g5ph7wf6ca-oc.a.run.app',
        crmApi: 'https://crmapitest-g5ph7wf6ca-oc.a.run.app',
        couponApi: 'https://couponapitest-g5ph7wf6ca-oc.a.run.app',
        devModeApi: 'https://devmodeapitest-g5ph7wf6ca-oc.a.run.app',
        paymentsApi: 'https://paymentsapitest-g5ph7wf6ca-oc.a.run.app',
        demApi: 'https://europe-west8-scb-website.cloudfunctions.net/demApiTest', // Real URL from the first deploy (2026-08-31)
        // Real URL from the first deploy (2026-08-25) — see the note on the Prod entry above.
        payAtTableApi: 'https://europe-west8-scb-website.cloudfunctions.net/payAtTableApiTest',
    },
};

// --- Unified API definition ---
export const SCBWEB_API = {
    booking: {
        baseName: 'bookingApi',
        endpoints: {
            addBooking: 'addBooking',
            addBookingByStaff: 'addBookingByStaff',
            recalculateMonthlyCapacity: 'availability/recalculate',
            modifyBooking: 'modifyBooking',
            cancelBooking: 'cancelBooking',
            markNoShow: 'markNoShow',
            requestReconfirmation: 'requestReconfirmation',
            reconfirmBooking: 'reconfirmBooking',
            markDeparted: 'markDeparted',
            notifyAccepted: 'notifyAccepted',
            notifyRefused: 'notifyRefused',
        },
    },
    crm: {
        baseName: 'crmApi',
        endpoints: {
            checkContactExistByEmail: 'checkContactExistByEmail',
        },
    },
    coupon: {
        baseName: 'couponApi',
        endpoints: {
            couponPersonalRequest: 'couponPersonalRequest',
            couponPersonalCheck: 'couponPersonalCheck',
        },
    },
    dem: {
        baseName: 'demApi',
        endpoints: {
            // Real path is `webhook/:provider/:secret` — the provider and the shared secret are
            // appended when the URL is registered in each provider's dashboard (see dem_api.ts).
            demWebhook: 'webhook',
        },
    },
    devMode: {
        baseName: 'devModeApi',
        endpoints: {
            checkPassword: 'checkPassword',
        },
    },
    payments: {
        baseName: 'paymentsApi',
        endpoints: {
            createIntent: 'payments/createIntent',
            createCheckout: 'payments/createCheckout',
            webhook: 'payments/webhook',
        },
    },
    // docs/pay-at-table-implementation-plan.md §2.2. `tilbyWebhook` is intentionally NOT listed here:
    // it's registered once with Tilby (POST /webhooks, scripts/register-tilby-webhook.ts) using
    // `${FUNCTION_BASE_URLS[version].payAtTableApi}/payattable/tilby/webhook/${webhookPathSecret}`,
    // never called through getApiUrl() from app code.
    payAtTable: {
        baseName: 'payAtTableApi',
        endpoints: {
            unlock: 'payattable/unlock',
            refreshCode: 'payattable/refreshCode',
            cancelSession: 'payattable/cancelSession',
            resolveConflict: 'payattable/resolveConflict',
            resolveCode: 'payattable/resolveCode',
            sessionRefresh: 'payattable/session/refresh',
            claim: 'payattable/claim',
            release: 'payattable/release',
            payStripeCreateIntent: 'payattable/pay/stripe/createIntent',
            paySatispayCreate: 'payattable/pay/satispay/create',
            stripeWebhook: 'payattable/stripe/webhook',
            satispayCallback: 'payattable/satispay/callback',
        },
    },
} as const;

export type ScbwebApiDomain = keyof typeof SCBWEB_API;

type AllEndpoints = {
    [D in ScbwebApiDomain]: (typeof SCBWEB_API)[D]['endpoints'][keyof (typeof SCBWEB_API)[D]['endpoints']];
}[ScbwebApiDomain];

export type ScbwebApiEndpoint = AllEndpoints;

// Internal reverse lookup: endpoint → baseName
const _endpointToBaseName: Record<string, string> = {};
for (const domain of Object.values(SCBWEB_API)) {
    for (const ep of Object.values(domain.endpoints)) {
        _endpointToBaseName[ep] = domain.baseName;
    }
}

/**
 * Builds a full API URL for the given version and endpoint.
 *
 * - For real deployments (Prod/Test), base URLs are resolved from FUNCTION_BASE_URLS.
 * - For local emulator, pass the emulator base path as `emulatorDomain`; the URL is then
 *   built using the legacy emulator pattern: `{emulatorDomain}/{baseName}{version}/{endpoint}`.
 *
 * Example (prod):     getApiUrl('Prod', 'addBooking')
 *                   → 'https://bookingapiprod-g5ph7wf6ca-ew.a.run.app/addBooking'
 * Example (emulator): getApiUrl('Test', 'addBooking', 'http://127.0.0.1:6101/scb-website/europe-west8')
 *                   → 'http://127.0.0.1:6101/scb-website/europe-west8/bookingApiTest/addBooking'
 */
export function getApiUrl(version: FunctionsVersion, endpoint: ScbwebApiEndpoint, emulatorDomain?: string): string {
    const baseName = _endpointToBaseName[endpoint];
    if (emulatorDomain) {
        return `${emulatorDomain}/${baseName}${version}/${endpoint}`;
    }
    return `${FUNCTION_BASE_URLS[version][baseName]}/${endpoint}`;
}
